import { useEffect, useState } from "react";
import { Link } from "wouter";
import { AlertTriangle, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { hasSeenSubscriptionInactive, subscribeSubscriptionInactive } from "@/lib/queryClient";
import type { InactiveSubscriptionReason } from "@shared/subscription-gate";

/**
 * What the app shows when the account's billing no longer grants access.
 *
 *   payment_failed      → past_due: the last charge failed; update the card
 *                         (billing page → Stripe Customer Portal)
 *   trial_ended         → trial over and no subscription was ever started;
 *                         add a card through Checkout
 *   subscription_ended  → a previous subscription was canceled / unpaid;
 *                         restart through Checkout
 *   team_member         → the inviting owner's billing lapsed; only the owner
 *                         can fix it
 *   generic             → a 402 arrived but the account state doesn't say why
 *                         (legacy plans) — point at billing
 *
 * The free PadSplit-revenue P&L survives every lapsed state (`pnl_base`), so
 * every variant links to it.
 */
export type SubscriptionInactiveVariant = InactiveSubscriptionReason | "team_member" | "generic";

export const FREE_PNL_PATH = "/app/financials";
export const CHECKOUT_PATH = "/app/onboarding/payment";
export const BILLING_PATH = "/app/account/billing";

interface NoticeCopy {
  title: string;
  body: string;
  cta: { label: string; href: string } | null;
}

export function subscriptionInactiveCopy(variant: SubscriptionInactiveVariant): NoticeCopy {
  switch (variant) {
    case "payment_failed":
      return {
        title: "Payment failed — update your card",
        body: "We couldn't charge the card on file, so paid features are paused. Update your card to restore full access.",
        cta: { label: "Update card", href: BILLING_PATH },
      };
    case "trial_ended":
      return {
        title: "Your trial has ended — add a card to continue",
        body: "Start your subscription through secure checkout to unlock your PadPulse plan again.",
        cta: { label: "Add a card", href: CHECKOUT_PATH },
      };
    case "subscription_ended":
      return {
        title: "Your subscription has ended — restart it to continue",
        body: "Restart your subscription through secure checkout to unlock your PadPulse plan again.",
        cta: { label: "Restart subscription", href: CHECKOUT_PATH },
      };
    case "team_member":
      return {
        title: "This account's subscription is inactive",
        body: "Paid features are paused until the account owner updates billing. Ask the account owner to restore access.",
        cta: null,
      };
    default:
      return {
        title: "Your subscription is inactive",
        body: "Paid features are paused. Update your billing to restore full access.",
        cta: { label: "Go to billing", href: BILLING_PATH },
      };
  }
}

/**
 * Paths that stay usable while the account's billing is lapsed: the free P&L
 * and the account/billing pages needed to fix the problem. Every other app
 * page is replaced by `SubscriptionInactiveScreen` for a lapsed owner.
 */
export function isAvailableWhileInactive(path: string): boolean {
  const p = path.split("?")[0].replace(/\/+$/, "");
  return p === FREE_PNL_PATH || p === `${FREE_PNL_PATH}/pnl` || p.startsWith("/app/account/") || p.startsWith("/app/onboarding");
}

/** Full-content panel shown in place of a gated page. */
export function SubscriptionInactiveScreen({ variant }: { variant: SubscriptionInactiveVariant }) {
  const copy = subscriptionInactiveCopy(variant);
  return (
    <div className="flex min-h-full items-start justify-center p-4 sm:p-8" data-testid="screen-subscription-inactive">
      <div className="mt-6 w-full max-w-lg rounded-lg border bg-background p-6 shadow-sm">
        <div className="flex items-center gap-2 text-red-600">
          <AlertTriangle className="h-5 w-5 shrink-0" aria-hidden="true" />
          <h2 className="text-lg font-semibold text-foreground" data-testid="text-subscription-inactive-title">
            {copy.title}
          </h2>
        </div>
        <p className="mt-3 text-sm text-muted-foreground" data-testid="text-subscription-inactive-body">
          {copy.body}
        </p>
        <div className="mt-5 flex flex-col gap-2 sm:flex-row">
          {copy.cta && (
            <Link href={copy.cta.href}>
              <Button className="w-full sm:w-auto" data-testid="button-subscription-inactive-cta">
                <CreditCard className="mr-1.5 h-4 w-4" aria-hidden="true" />
                {copy.cta.label}
              </Button>
            </Link>
          )}
          <Link href={FREE_PNL_PATH}>
            <Button variant="outline" className="w-full sm:w-auto" data-testid="button-subscription-inactive-pnl">
              Open your free P&amp;L
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}

/**
 * Slim app-wide banner. Visible when the account's own state is lapsed
 * (`ownVariant`, on pages that stay usable) OR as soon as any request gets a
 * 402 `SUBSCRIPTION_INACTIVE` (e.g. a team member whose owner lapsed — their
 * own row can't tell us). `fallbackVariant` names the 402 case.
 */
export function SubscriptionInactiveBanner({
  ownVariant,
  fallbackVariant,
}: {
  ownVariant: SubscriptionInactiveVariant | null;
  fallbackVariant: SubscriptionInactiveVariant;
}) {
  const [seen, setSeen] = useState<boolean>(() => hasSeenSubscriptionInactive());
  useEffect(() => {
    if (hasSeenSubscriptionInactive()) setSeen(true);
    return subscribeSubscriptionInactive(() => setSeen(true));
  }, []);

  const variant = ownVariant ?? (seen ? fallbackVariant : null);
  if (!variant) return null;
  const copy = subscriptionInactiveCopy(variant);
  return (
    <div
      role="alert"
      className="flex flex-col gap-2 border-b border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-800 sm:flex-row sm:items-center dark:border-red-900 dark:bg-red-950/40 dark:text-red-200"
      data-testid="banner-subscription-inactive"
    >
      <div className="flex flex-1 items-center gap-2">
        <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="font-semibold" data-testid="text-subscription-inactive-banner">{copy.title}</span>
        <span className="hidden md:inline">· Your free P&amp;L is still available.</span>
      </div>
      <div className="flex gap-2">
        {copy.cta && (
          <Link href={copy.cta.href}>
            <Button size="sm" data-testid="button-subscription-inactive-banner-cta">{copy.cta.label}</Button>
          </Link>
        )}
        <Link href={FREE_PNL_PATH}>
          <Button size="sm" variant="outline" data-testid="button-subscription-inactive-banner-pnl">Free P&amp;L</Button>
        </Link>
      </div>
    </div>
  );
}
